<template>
  <div class="c-field" :class="classes">
    <CValidation :validators="allValidators" :value="model">
      <CLabel v-if="label" :for="id">{{ label }}</CLabel>

      <div class="c-field-control-wrapper">
        <span :class="{ 'c-field-control': !slots.default, 'c-field-control-no-border-hover': type === 'radiolist' || type === 'checklist' }" ref="control" @click="focus">
          <span v-if="$slots.prefix || type === 'amount'" class="c-field-prefix">
            <slot name="prefix">
              <template v-if="type === 'amount'">{{
                $t("general.currencySymbol")
              }}</template>
            </slot>
          </span>

          <slot v-bind="inputBindings">
            <component v-if="type" :is="INPUT_COMPONENTS[type]" v-bind="inputBindings">
              <template #nullOption>
                <slot name="nullOption" />
              </template>
              <template #option="{ option }">
                <slot name="option" :option />
              </template>
            </component>
          </slot>

          <span v-if="$slots.suffix" class="c-field-suffix">
            <slot name="suffix" />
          </span>
        </span>
      </div>
    </CValidation>

    <p v-if="warning" class="c-field-warning">{{ warning }}</p>
    <p v-if="help" class="c-field-help">{{ help }}</p>

    <slot name="footer" />
  </div>
</template>

<script setup lang="ts">
  import { VueDatePickerProps } from '@vuepic/vue-datepicker';
  import { omit } from 'lodash';
  import { computed, nextTick, ref, useAttrs, useSlots, watch } from 'vue';
  import FileCategory from '../../constants/FileCategory';
  import { uid } from '../../utils';
  import { elementRef } from '../../utils/component';
  import { getFormController } from '../../utils/form/form';
  import { dontCare,
           email,
           numeric as numericValidator,
           required as requiredValidator,
           ValidatorMap } from '../../utils/form/validation';
  import CLabel from './CLabel.vue';
  import CCheckBox from './CCheckBox.vue';
  import CCheckList from './CCheckList.vue';
  import CDatePicker from './CDatePicker.vue';
  import CFileUpload from './CFileUpload.vue';
  import CInput, { Mask } from './CInput.vue';
  import CRadioList from './CRadioList.vue';
  import CTelInput from './CTelInput.vue';
  import CTextArea from './CTextArea.vue';
  import CValidation from './CValidation.vue';

  const INPUT_COMPONENTS: { [key: string]: any } = {
    text: CInput,
    search: CInput,
    email: CInput,
    password: CInput,
    number: CInput,
    amount: CInput,
    tel: CTelInput,
    date: CDatePicker,
    daterange: CDatePicker,
    time: CDatePicker,
    duration: CDatePicker,
    datetime: CDatePicker,
    year: CDatePicker,
    month: CDatePicker,
    week: CDatePicker,
    textarea: CTextArea,
    // select: ...,
    checkbox: CCheckBox,
    checklist: CCheckList,
    radio: CRadioList, // alias to conform to native type
    radiolist: CRadioList,
    radiobar: CRadioList,
    file: CFileUpload,
  };

  export type Type = keyof typeof INPUT_COMPONENTS;

  const props = defineProps<{
    // common
    type?: Type;
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    validators?: ValidatorMap;
    required?: boolean;
    numeric?: boolean;
    help?: string;
    autoSubmitOnChange?: boolean;
    // text
    mask?: Mask;
    keepMask?: boolean;
    maxlength?: number;
    // number
    min?: number;
    max?: number;
    step?: number;
    // date
    config?: VueDatePickerProps;
    // textarea
    rows?: number;
    // selection
    options?: Array<any>;
    disabledOptions?: Array<any>;
    selectAll?: boolean;
    nullable?: boolean;
    // file
    multiple?: boolean;
    accept?: string;
    category?: FileCategory;
    icon?: string;
  }>();

  defineExpose({ focus });

  const model = defineModel();

  const emit = defineEmits<{
    'internal-value-change': [any];
  }>();

  const slots = useSlots();

  const control = elementRef();

  const warning = ref('');

  const id = uid();

  const form = getFormController();

  const disabledInternal = computed(() => props.disabled || form?.disabled || false);

  const classes = computed(() => ({
    [`c-field-${props.type}`]: true,
    'c-field-has-warning': warning.value,
    'c-field-disabled': disabledInternal.value,
  }));

  const allValidators = computed(() => ({
    required: props.required ? requiredValidator : dontCare,
    email: props.type === 'email' ? email : dontCare,
    numeric: props.numeric ? numericValidator : dontCare,
    ...props.validators,
  }));

  const attrs = useAttrs();

  const inputBindings = computed(() => ({
    ...omit(props, 'label', 'validators', 'required'),
    ...attrs,
    id,
    disabled: disabledInternal.value,
    modelValue: model.value,
    'onUpdate:modelValue': (v) => (model.value = v),
    'onUpdate:warning': (v) => (warning.value = v),
    // for DatePicker internal value manipulation
    onInternalValueChange: (v) => {
      emit('internal-value-change', v);
    },
  }));

  function focus() {
    control.value?.querySelector(props.type === 'textarea' ? 'textarea' : 'input')?.focus();
  }
  
  const formController = getFormController();

  watch(() => model.value, async () => {
    if (!props.autoSubmitOnChange || !formController) {
      return;
    }
    
    await nextTick();
    await formController.submit();
  })
</script>

<style lang="scss">
.c-field {
  margin-top: 0.5rem;

  &:not(:last-child) {
    margin-bottom: 1rem;
  }

  &-control-wrapper {
    position: relative;
  }

  &-control {
    display: flex;
    align-items: center;
    border-radius: $input-radius;
    color: $neutral-800;
    font-size: 1rem;
    line-height: 1.125;
    background-color: $input-bg;
    border: 1px solid $input-border;
    box-shadow: inset 0 1px 2px mix($input-border, $input-bg, 50%);
    overflow: hidden;
    .c-date-picker {
      .dp__input {
        border: none;
      }
    }

    > * {
      flex-grow: 1;
      padding: $input-padding;
    }

    &:hover {
      border-color: $input-border-hover;
    }

    &:has(input:focus) {
      border-color: $input-border-active;
      box-shadow: 0 0 0 2px mix($input-border-active, $input-bg, 25%);
    }
  }

  &-prefix {
    flex-grow: 0;
    padding-right: 0;
    line-height: inherit;
  }

  &-suffix {
    flex-grow: 0;
    padding-left: 0;
  }

  &-warning {
    color: $warning;
  }

  &-has-warning .c-field-control {
    border-color: $warning;

    &:focus-within {
      box-shadow: 0 0 0 2px mix($warning, $input-bg, 25%);
    }
  }

  &-help {
    font-size: 0.75rem;
  }

  .c-validation-failed .c-field-control {
    border-color: $danger;

    &:focus-within {
      box-shadow: 0 0 0 2px mix($danger, $input-bg, 25%);
    }
  }

  &#{&}-disabled {
    .c-field-control,
    .c-input {
      cursor: not-allowed;
      color: $input-fg-disabled;
      background-color: $input-bg-disabled;
      border-color: $input-bg-disabled;
      box-shadow: none;
    }
  }

  .c-options-dropdown {
    .c-button {
      font-weight: unset;
      &:not(.c-button-disabled) {
        color: $neutral-800;
      }
    }

    .c-icon.c-icon-chevron-right {
      width: 0.75rem;
      height: 0.75rem;
    }
  }
}

.c-field-control-no-border-hover {
  &.c-field-control:hover {
    border-color: $input-border;
  }
}
</style>
